import i18n from 'helpers/i18n';
import messages from './messages';
import TextButton from 'components/TextButton';
import useDebouncedEffect from 'hooks/useDebouncedEffect';
import { memo, useState } from 'react';
import { Box } from 'components/Box';
import { Flex } from 'components/Flex';
import { StatusIndicator } from './StatusIndicator';
import { abbrToUppercase } from 'helpers/dimension';
import { Domain } from 'types/api/envScales/Domain';
import { Dimension } from 'types/api/envScales/Dimension';
import { IndicatorScoreInput } from '../IndicatorScoreInput';
import { DimensionScoreInput } from '../DimensionScoreInput';
import { IndicatorScore } from 'types/api/envScales/IndicatorScore';
import {
  StatusWrapper,
  LineBreak,
  Name,
  DataGrid,
  WarningScore
} from './Styled';
import { updateObservationCycleScores } from 'actions/envScales/observationCycleScores';
import { SectionWithBorder } from 'components/Measure/View/Styled';
import type { IndicatorScoreState, IndicatorScoreValues } from './utils';
import { ObservationCycle } from 'types/api/envScales/ObservationCycle';
import {
  useValidations,
  useServerFieldError
} from 'components/Measure/View/ValidationsContext';
import { Icon } from 'semantic-ui-react';
import * as yup from 'yup';
import Warning from 'components/Measure/Warning';

import {
  mapIndicatorScores,
  getInitialIndicators,
  calculateSuggestions,
  getScoreWarnings,
  getInformalScoreWarnings
} from './utils';

export interface DimensionCardProps {
  /** Observation Cycle. */
  cycle: ObservationCycle;

  /** Initial dimension score. */
  initialScore: number | null;

  /** Initial array of indicator scores. */
  initialIndicators: IndicatorScore[];

  /** Domain. */
  domain: Domain;

  /** Dimension. */
  dimension: Dimension;

  /** If `true`, the card will show all score input controls. */
  isExpanded: boolean;

  /** Indicates if the observation is informal. */
  isInformal: boolean;

  /** Indicates if the observation notes feature is enabled. */
  isNotesEnabled: boolean;

  /** Latest observation notes for this dimension. */
  observationNotes: string;

  /** Callback fired when `isExpanded` changes. */
  onExpandCollapse: (dimensionId: number, isExpanded: boolean) => void;
}

function DimensionCardView({
  cycle,
  initialScore,
  initialIndicators,
  domain,
  dimension,
  isInformal,
  isExpanded,
  isNotesEnabled,
  observationNotes,
  onExpandCollapse
}: DimensionCardProps) {
  const [touched, setTouched] = useState(false);
  const [scoreTouched, setScoreTouched] = useState(false);
  const [score, setScore] = useState<number | null>(initialScore);
  const [indicatorScores, setIndicatorScores] = useState<IndicatorScoreState>(
    getInitialIndicators(initialIndicators)
  );
  const [scoreWarnings, setScoreWarnings] = useState<Array<string>>([]);

  const { showErrorStatus } = useValidations();
  const dimensionScoreError = useServerFieldError(
    `cycles.${cycle.cycle_number}.scores.${dimension.t_name}`,
    score,
    isInformal ? yup.number().nullable() : yup.number().required()
  );

  // No single flat-path key covers all indicators, so this scans live
  // local state directly instead of one useServerFieldError call.
  const indicatorsHaveError =
    !isInformal &&
    showErrorStatus &&
    dimension.indicators.some(indicator => {
      const currentValue = indicatorScores[indicator.id];
      return currentValue === null || currentValue === undefined;
    });

  function hasMeaningfulNotes(value: string) {
    return value.replace(/•/g, '').trim().length > 0;
  }

  const indicators = mapIndicatorScores(indicatorScores);

  const indicatorsComplete = indicators.length === dimension.indicators.length;
  const suggestions = calculateSuggestions(
    indicatorsComplete,
    indicators.filter(ind => ind.score !== -1)
  );
  const showWarning = scoreTouched && !indicatorsComplete;

  useDebouncedEffect(
    () => {
      if (!touched) {
        return;
      }

      const payload = {
        observation_cycle_score: {
          dimension_id: dimension.id,
          dimension_score: score,
          indicator_scores: mapIndicatorScores(indicatorScores)
        }
      };

      updateObservationCycleScores(cycle.id, payload);
    },
    1000,
    [score, indicatorScores]
  );

  function handleExpandCollapse() {
    onExpandCollapse(dimension.id, !isExpanded);
  }

  function handleScoreChange(value: number | null) {
    setScoreTouched(true);
    setTouched(true);

    if (indicatorsComplete) {
      setScore(value);

      if (value !== null) {
        setScoreWarnings(
          isInformal
            ? getInformalScoreWarnings(value, dimension)
            : getScoreWarnings(value, dimension)
        );
      } else {
        setScoreWarnings([]);
      }
    }
  }

  function handleIndicatorChange(
    indicatorId: number,
    value: IndicatorScoreValues
  ) {
    setIndicatorScores({ ...indicatorScores, [indicatorId]: value });
    setScore(null);
    setTouched(true);
  }

  function formatScore(scoreValue: number | null) {
    if (!isInformal || !scoreValue) {
      return scoreValue;
    }

    if (scoreValue >= 6) {
      return i18n.ft(messages.high);
    } else if (scoreValue >= 3) {
      return i18n.ft(messages.mid);
    } else {
      return i18n.ft(messages.low);
    }
  }

  const ariaLabelId = `dimension-score-${dimension.t_name}`;

  return (
    <SectionWithBorder className="mt-2">
      <Flex
        wrap="wrap"
        justify="space-between"
        onClick={handleExpandCollapse}
        className="cursor-pointer"
      >
        <TextButton color="darkBlue">
          {dimension.name} ({abbrToUppercase(dimension.abbreviation)})
        </TextButton>
        <StatusWrapper
          hasErrors={Boolean(dimensionScoreError) || indicatorsHaveError}
        >
          <span>{i18n.ft(messages.indicators)}</span>
          <StatusIndicator
            completed={indicatorsComplete}
            hasError={indicatorsHaveError}
          />
          <Box as="span" ml={{ _: '8px', sm: '48px' }}>
            {isInformal ? i18n.ft(messages.range) : i18n.ft(messages.score)}
          </Box>
          <StatusIndicator
            completed={Boolean(score)}
            hasError={Boolean(dimensionScoreError)}
            variant={isInformal ? 'pill' : 'circle'}
          >
            {formatScore(score)}
          </StatusIndicator>
        </StatusWrapper>
      </Flex>

      {isExpanded && (
        <>
          <LineBreak />

          {isNotesEnabled && (
            <>
              <div className="font-bold">
                {i18n.ft(messages.observationNotes)}
              </div>

              <div className="mt-2 text-[#58697B] whitespace-pre-line">
                {hasMeaningfulNotes(observationNotes)
                  ? observationNotes
                  : i18n.ft(messages.noNotesYet)}
              </div>

              <LineBreak />
            </>
          )}

          <DataGrid>
            {dimension.indicators.map(indicator => {
              return (
                <IndicatorScoreInput
                  key={indicator.id}
                  indicator={indicator}
                  value={indicatorScores[indicator.id]}
                  domain={domain}
                  onChange={handleIndicatorChange}
                  cycleNumber={cycle.cycle_number}
                  dimension={dimension.t_name}
                  isInformal={isInformal}
                />
              );
            })}
            {showWarning && (
              <WarningScore role="alert">
                {i18n.ft(messages.indicatorError)}
              </WarningScore>
            )}
          </DataGrid>

          <Box mt={showWarning ? '12px' : '48px'} mb="16px">
            <DataGrid>
              <div>
                <Warning
                  warningTexts={scoreWarnings}
                  maxWidth={290}
                  dismissible
                  onDismiss={() => setScoreWarnings([])}
                  floating={false}
                  overlapChildren={true}
                >
                  <Name id={ariaLabelId}>
                    {isInformal
                      ? i18n.ft(messages.overallRange)
                      : i18n.ft(messages.score)}
                    {dimensionScoreError && (
                      <>
                        <Icon
                          style={{ marginLeft: '5px' }}
                          color="red"
                          name="exclamation circle"
                        />
                        <span className="text-scale-red text-sm font-normal block mt-1 md:inline md:mt-0 md:ml-1">
                          {i18n.ft(messages.errors.required)}
                        </span>
                      </>
                    )}
                  </Name>
                </Warning>
              </div>

              <DimensionScoreInput
                value={score}
                ariaLabelledby={ariaLabelId}
                onChange={handleScoreChange}
                suggestedScores={suggestions}
                isInformal={isInformal}
              />
            </DataGrid>
          </Box>
        </>
      )}
    </SectionWithBorder>
  );
}

export const DimensionCard = memo(DimensionCardView);
